import * as React from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { apiRequest } from '../../../../APIClient';
import { OBContact, PartnerTypeEnum } from '../../../../types';
import { ONBOARDING_ACTIONS } from '../../../../actions/onboarding';
import { sessionSelector } from '../../../../reducers/session';
import { PageTitle } from '../../../../widgets/PageTitle';
import Viewport from '../../../../widgets/Viewport';
import ViewHeader from '../../../../widgets/ViewHeader';
import { ContactValidationSchema } from '../../validationSchemas';
import {
  cleanPermissions,
  ContactFormValues,
  EMPTY_CONTACT,
  EMPTY_SHIPPING_CONTACT,
} from './constants';
import { OnboardingContactForm } from './Form';
import { ROUTE_PATH } from '../../../../constants';
import {
  getPrimaryContactId,
  onboardingSelector,
} from '../../../../reducers/onboarding';
import { chainApiDispatches } from '../../../../utils';
import { ScrollSectionKeys } from '../../site-config/OnboardingSiteConfig';

export const OnboardingContactAddView: React.FunctionComponent = () => {
  const customerPartnerType = useSelector(
    sessionSelector.getCustomerPartnerType,
  );

  const contacts = useSelector(onboardingSelector.getContacts);

  const initialValues = React.useMemo(
    () =>
      customerPartnerType === PartnerTypeEnum.MSP
        ? EMPTY_SHIPPING_CONTACT
        : EMPTY_CONTACT,
    [customerPartnerType],
  );

  const dispatch = useDispatch();

  const onSubmit = React.useCallback(
    async (values: ContactFormValues) => {
      const contact = cleanPermissions(values);
      const primaryContactId = getPrimaryContactId(contacts);
      if (contact.contactType === 'primary' && primaryContactId) {
        // Reassign previous primary contact to normal because we only allow one primary contact
        const changedPrimary: OBContact = {
          ...contacts[primaryContactId],
          contactType: 'normal',
        };
        dispatch(
          ONBOARDING_ACTIONS.SET_CONTACT(primaryContactId, changedPrimary),
        );
        // We still need to wait for the response from creation back for the UUID
        await chainApiDispatches(dispatch, [
          apiRequest.updateOBContact(primaryContactId, changedPrimary),
          apiRequest.createOBContact(contact),
        ]);
      } else {
        await dispatch(apiRequest.createOBContact(contact));
      }
    },
    [dispatch, contacts],
  );

  return (
    <Viewport>
      <Breadcrumb>
        <BreadcrumbItem>
          <Link to={ROUTE_PATH.ONBOARDING.MAIN}>Onboarding Overview</Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Add Contact</BreadcrumbItem>
      </Breadcrumb>
      <PageTitle title="Add Contact" />
      <ViewHeader title="Add Contact" />
      <Formik<ContactFormValues>
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={ContactValidationSchema}
      >
        <OnboardingContactForm
          showSaveAndAdd
          view={ScrollSectionKeys.CONTACTS}
        />
      </Formik>
    </Viewport>
  );
};
